import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import ReactMarkdown from 'react-markdown';

/*
  NOTE: Add these two images to your public/ folder:
    - hampton-inn.jpg   (Hampton Inn photo)
    - marriott.jpg      (Courtyard by Marriott photo)
*/

const AnchorSVG = ({ className = '' }: { className?: string }) => (
  <svg className={className} viewBox="0 0 40 52" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="20" cy="7" r="5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
    <circle cx="20" cy="7" r="2" fill="currentColor"/>
    <line x1="4" y1="14" x2="36" y2="14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="20" y1="12" x2="20" y2="40" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M20 40 C13 38 5 33 5 26" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
    <path d="M20 40 C27 38 35 33 35 26" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
    <path d="M5 26 C5 46 35 46 35 26" stroke="currentColor" strokeWidth="1.5" fill="none"/>
  </svg>
);

interface QAItem {
  id: string;
  question: string;
  answer: string;
  order: number;
}

const FALLBACK_ITEMS: QAItem[] = [
  { id: '1', question: 'Where do I park?',            answer: 'There will be valet parking for all vehicles.', order: 1 },
  { id: '2', question: 'What will the weather be like?', answer: "During this time of year it will likely be mid 70s but we'll be on the water so expect some wind.", order: 2 },
];

const QA: React.FC = () => {
  const [qaItems, setQAItems] = useState<QAItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const q = query(collection(db, 'qaItems'), orderBy('order', 'asc'));
        const snap = await getDocs(q);
        const items: QAItem[] = [];
        snap.forEach(doc => items.push({ id: doc.id, ...doc.data() } as QAItem));
        setQAItems(items.length > 0 ? items : FALLBACK_ITEMS);
      } catch (e) {
        console.error('Error fetching Q&A items:', e);
        setQAItems(FALLBACK_ITEMS);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <main>
      {/* PAGE HEADER */}
      <div className="page-header">
        <AnchorSVG className="page-header__anchor" />
        <p className="page-header__eyebrow">Everything you need to know</p>
        <h1 className="page-header__title">Q &amp; A</h1>
        <div className="page-header__rule page-rule">
          <div className="rule-line rule-line--short"></div>
          <div className="rule-diamond"></div>
          <div className="rule-line rule-line--short"></div>
        </div>
      </div>

      <section className="qa-section">
        <div className="qa-inner">

          {/* HOTELS */}
          <p className="section-eyebrow" style={{ marginBottom: '2rem' }}>Where to Stay</p>
          <div className="hotels-grid">
            <div className="hotel-card">
              <img src={`${process.env.PUBLIC_URL}/hampton-inn.png`} alt="Hampton Inn Detroit/Roseville" className="hotel-card__img" />
              <div className="hotel-card__body">
                <h3 className="hotel-card__name">Hampton Inn Detroit/Roseville in Clinton Township</h3>
                <p className="hotel-card__detail">
                  <strong>Phone:</strong> 586.792.1500<br />
                  <strong>Address:</strong> 33680 Gratiot Avenue, Clinton Township, MI 48035<br />
                  <strong>Distance:</strong> 12 mins
                </p>
                <a
                  href="https://www.hilton.com/en/hotels/dttrvhx-hampton-detroit-roseville/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hotel-card__link"
                >
                  Book a Room →
                </a>
              </div>
            </div>

            <div className="hotel-card">
              <img src={`${process.env.PUBLIC_URL}/marriott.png`} alt="Courtyard Marriott Warren" className="hotel-card__img" />
              <div className="hotel-card__body">
                <h3 className="hotel-card__name">Courtyard Marriott Warren</h3>
                <p className="hotel-card__detail">
                  <strong>Phone:</strong> 586.751.5777<br />
                  <strong>Address:</strong> 30190 Van Dyke Avenue, Warren, MI 48093<br />
                  <strong>Distance:</strong> 14 mins
                </p>
                <a
                  href="https://www.marriott.com/en-us/hotels/dttwr-courtyard-detroit-warren/overview/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hotel-card__link"
                >
                  Book a Room →
                </a>
              </div>
            </div>
          </div>

          {/* DIVIDER */}
          <div className="section-divider page-rule" style={{ margin: '3.5rem 0' }}>
            <div className="rule-line rule-line--flex"></div>
            <div className="rule-diamond"></div>
            <div className="rule-line rule-line--flex"></div>
          </div>

          {/* Q&A ITEMS */}
          <p className="section-eyebrow" style={{ marginBottom: '2rem' }}>Frequently Asked</p>

          {loading ? (
            <p style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>Loading…</p>
          ) : (
            <div className="qa-list">
              {qaItems.map(item => (
                <div key={item.id} className="qa-item">
                  <span className="qa-question">{item.question}</span>
                  <div className="qa-answer">
                    <ReactMarkdown
                      components={{
                        a: ({ node, ...props }) => (
                          <a {...props} target="_blank" rel="noopener noreferrer" />
                        ),
                        p: ({ node, ...props }) => <p {...props} />,
                      }}
                    >
                      {item.answer}
                    </ReactMarkdown>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* DIVIDER */}
          <div className="section-divider page-rule" style={{ margin: '3.5rem 0' }}>
            <div className="rule-line rule-line--flex"></div>
            <div className="rule-diamond"></div>
            <div className="rule-line rule-line--flex"></div>
          </div>

          {/* AFTERPARTY SECTION */}
          <div id="afterparty">
            <p className="section-eyebrow" style={{ marginBottom: '2rem' }}>The Afterparty</p>
            <div className="qa-list">
              <div className="qa-item">
                <span className="qa-question">Will there be an afterparty?</span>
                <div className="qa-answer">
                  <p>Yes! Since the official wedding festivities end at 3:00pm we will be holding an afterparty beginning at 4:30pm with snacks, drinks, and yard games to keep the celebration going. No dress code, come dressed comfortably.</p>
                  <p style={{ marginTop: '1rem' }}>
                    <a href="/afterparty" style={{ color: 'var(--gold)', textDecoration: 'underline', fontWeight: 600 }}>
                      View More Details &rarr;
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>
    </main>
  );
};

export default QA;
